import { defineStore } from "pinia";
import { ref } from "vue";
import { INSPECTION_LINKS_ENDPOINT } from "@/constants/Endpoints";
import axios from "axios";
import { LOGGER } from "@/util/logger";
import { clone } from "@/util/clone";
import { MODULE_TYPES } from "@/constants/InspectionConstants";
import { SCOUR_COMP } from "@/data/underwaterInspection";

export const useWaterwayStore = defineStore("waterway", () => {
  //data is loaded either from idb(pouch db for inspection module) or service(for management module)
  let pageData = ref({});

  const loadDataObject = async (moduleType, brkey, inspectionId, structure) => {
    if (moduleType === MODULE_TYPES.INSPECTION) {
      //if moduleType is inspection, load data from pouchDB
      convertToInspModuleObject(structure);
    } else if (moduleType === MODULE_TYPES.MANAGEMENT && brkey && inspectionId) {
      //if moduleType is management, load data from service call
      await getInspLinkDataObject(brkey, inspectionId);
    }
  };

  const getWaterwayObject = () => {
    return clone(pageData.value);
  };

  const saveData = async (moduleType, value, structure) => {
    let isSuccess = false;
    if (moduleType === MODULE_TYPES.INSPECTION) {
      convertToSaveInspModuleObject(value, structure);
      isSuccess = true;
    } else if (moduleType === MODULE_TYPES.MANAGEMENT) {
      isSuccess = await updateData(convertToSaveInspLinkObject(value));
    }
    return isSuccess;
  };

  const updateData = async (payload) => {
    let isSuccess = true;
    const url = INSPECTION_LINKS_ENDPOINT.UPDATE_WATERWAY;
    await axios
      .put(url, payload)
      .then((response) => {
        LOGGER.logEvent(LOGGER.EventAction.Update, LOGGER.EventCat.RequestResponse, "Update Waterway", response);
        convertToInspLinkObject(response.data.data);
      })
      .catch((e) => {
        LOGGER.logException(e);
        isSuccess = false;
      });
    return isSuccess;
  };

  const convertToSaveInspLinkObject = (waterway) => {
    let scourComp = {};
    scourComp.brkey = waterway.brkey;
    scourComp.inspectionId = waterway.inspectionId;
    scourComp.scbiSource = waterway.scbiSource;
    scourComp.overallOSA = waterway.overallOSA;
    scourComp.scourCalculated = waterway.scourCalculated;
    scourComp.streamBedMaterial1 = waterway.streamBedMaterial1;
    scourComp.streamBedMaterial2 = waterway.streamBedMaterial2;
    scourComp.streamBedMaterialDesc = waterway.streamBedMaterialDesc;
    scourComp.unitsInspectedQty = waterway.unitsInspectedQty;
    scourComp.nabLocation = waterway.nabLocation;
    scourComp.fabLocation = waterway.fabLocation;
    scourComp.usLeftWWPresence = waterway.usLeftWWPresence;
    scourComp.usLeftWWCond = waterway.usLeftWWCond;
    scourComp.usRightWWPresence = waterway.usRightWWPresence;
    scourComp.usRightWWCond = waterway.usRightWWCond;
    scourComp.horDebrisStartPct = waterway.horDebrisStartPct;
    scourComp.horDebrisEndPct = waterway.horDebrisEndPct;
    scourComp.vertDebrisStartPct = waterway.vertDebrisStartPct;
    scourComp.vertDebrisEndPct = waterway.vertDebrisEndPct;
    scourComp.overTopping = waterway.overTopping;
    scourComp.trafficDelay = waterway.trafficDelay;
    scourComp.overTopRisk = waterway.overTopRisk;
    scourComp.overtopDate = waterway.overtopDate;
    scourComp.highWaterElev = waterway.highWaterElev;
    scourComp.highWaterDate = waterway.highWaterDate;
    scourComp.newHighWater = waterway.newHighWater;
    scourComp.scpoaReqD = waterway.scpoaReqD;
    return {
      // identifiers
      brkey: waterway.brkey,
      inspectionId: waterway.inspectionId,
      updateUserId: waterway.updateUserId,
      updateDateTime: waterway.updateDateTime,
      functionClass: waterway.funcClass,
      servTypUnd: waterway.servTypUnd,
      navCntrol: waterway.navCntrol,
      deptMainStrucConfig: waterway.deptMainStrucConfig,

      // comments
      inspectionComments: waterway.inspectionComments,

      // page values
      scourComp: scourComp,
      scourCMeasures: waterway.scourCMeasures,
      scourPossCMeasures: waterway.scourPossCMeasures,
      structureUnits: waterway.structureUnits,
      inspectionsUnderwater: waterway.inspectionsUnderwater,
      scourCriticalDuration: waterway.scourCriticalDuration,
      chanRatingDuration: waterway.chanRatingDuration,
      scourCritical: waterway.scourCritical,
      scourCriticalCat: waterway.scourCriticalCat,
      chanRating: waterway.chanRating,
      culvRating: waterway.culvRating,
      subRating: waterway.subRating,
      pierprot: waterway.pierprot,
      wateradeq: waterway.wateradeq,
      chanProtRating: waterway.chanProtRating,
      scourCondRating: waterway.scourCondRating,
      underwaterRating: waterway.underwaterRating,
      scourVulnerability: waterway.scourVulnerability,
      scpoaReqD: waterway.scpoaReqD,
    };
  };

  const convertToSaveInspModuleObject = (waterwayValue, structure) => {
    // If there is no existing scourComp object, we need to create one
    if (!structure?.scourComp) {
      let newScourComp = clone(SCOUR_COMP);
      newScourComp.brkey = structure.structureBridge.brkey;
      newScourComp.inspectionId = structure.inspection.inspectionId;
      structure.scourComp = clone(newScourComp);
    }

    // scourComp
    structure.scourComp.scbiSource = waterwayValue.scbiSource;
    structure.scourComp.overallOSA = waterwayValue.overallOSA;
    structure.scourComp.scourCalculated = waterwayValue.scourCalculated;
    structure.scourComp.streamBedMaterial1 = waterwayValue.streamBedMaterial1;
    structure.scourComp.streamBedMaterial2 = waterwayValue.streamBedMaterial2;
    structure.scourComp.streamBedMaterialDesc = waterwayValue.streamBedMaterialDesc;
    structure.scourComp.unitsInspectedQty = waterwayValue.unitsInspectedQty;
    structure.scourComp.nabLocation = waterwayValue.nabLocation;
    structure.scourComp.fabLocation = waterwayValue.fabLocation;
    structure.scourComp.usLeftWWPresence = waterwayValue.usLeftWWPresence;
    structure.scourComp.usLeftWWCond = waterwayValue.usLeftWWCond;
    structure.scourComp.usRightWWPresence = waterwayValue.usRightWWPresence;
    structure.scourComp.usRightWWCond = waterwayValue.usRightWWCond;
    structure.scourComp.horDebrisStartPct = waterwayValue.horDebrisStartPct;
    structure.scourComp.horDebrisEndPct = waterwayValue.horDebrisEndPct;
    structure.scourComp.vertDebrisStartPct = waterwayValue.vertDebrisStartPct;
    structure.scourComp.vertDebrisEndPct = waterwayValue.vertDebrisEndPct;
    structure.scourComp.overTopping = waterwayValue.overTopping;
    structure.scourComp.trafficDelay = waterwayValue.trafficDelay;
    structure.scourComp.overTopRisk = waterwayValue.overTopRisk;
    structure.scourComp.overtopDate = waterwayValue.overtopDate;
    structure.scourComp.highWaterElev = waterwayValue.highWaterElev;
    structure.scourComp.highWaterDate = waterwayValue.highWaterDate;
    structure.scourComp.newHighWater = waterwayValue.newHighWater;

    // userInspection
    structure.userInspection.chanProtRating = waterwayValue.chanProtRating;
    structure.userInspection.scourCondRating = waterwayValue.scourCondRating;
    structure.userInspection.underwaterRating = waterwayValue.underwaterRating;
    structure.userInspection.scourVulnerability = waterwayValue.scourVulnerability;
    structure.userInspection.scpoaReqD = waterwayValue.scpoaReqD;

    // inspection
    structure.inspection.scourCritical = waterwayValue.scourCritical;
    structure.inspection.scourCriticalCat = waterwayValue.scourCriticalCat;
    structure.inspection.chanRating = waterwayValue.chanRating;
    structure.inspection.culvRating = waterwayValue.culvRating;
    structure.inspection.subRating = waterwayValue.subRating;
    structure.inspection.pierprot = waterwayValue.pierprot;
    structure.inspection.wateradeq = waterwayValue.wateradeq;

    // inspectionConditionHist
    structure.inspectionConditionHist.scourCriticalDuration = waterwayValue.scourCriticalDuration;
    structure.inspectionConditionHist.scourCriticalDurationChanges = waterwayValue.scourCriticalDurationChanges;
    structure.inspectionConditionHist.chanRatingDuration = waterwayValue.chanRatingDuration;
    structure.inspectionConditionHist.chanRatingDurationChanges = waterwayValue.chanRatingDurationChanges;

    structure.scourCMeasures = waterwayValue.scourCMeasures;
    structure.scourPossCMeasures = waterwayValue.scourPossCMeasures;
    structure.structureUnits = waterwayValue.structureUnits;
    structure.inspectionsUnderwater = waterwayValue.inspectionsUnderwater;
  };

  const convertToInspModuleObject = (structure) => {
    pageData.value = {
      brkey: structure?.structureBridge?.brkey,
      inspectionId: structure?.inspection?.inspectionId,
      updateUserId: structure?.inspection?.updateUserId,
      updateDateTime: structure?.inspection?.updateDateTime,
      functionClass: structure?.structureRoadways?.find((a) => a.onUnder === "1")?.funcClass,
      servTypUnd: structure?.structureBridge?.servTypUnd,
      navCntrol: structure?.structureBridge?.navCntrol,
      deptMainStrucConfig: structure?.userBridge?.deptMainStrucConfig,

      scbiSource: structure?.scourComp?.scbiSource,
      overallOSA: structure?.scourComp?.overallOSA,
      scourCalculated: structure?.scourComp?.scourCalculated,
      streamBedMaterial1: structure?.scourComp?.streamBedMaterial1,
      streamBedMaterial2: structure?.scourComp?.streamBedMaterial2,
      streamBedMaterialDesc: structure?.scourComp?.streamBedMaterialDesc,
      unitsInspectedQty: structure?.scourComp?.unitsInspectedQty,
      nabLocation: structure?.scourComp?.nabLocation,
      fabLocation: structure?.scourComp?.fabLocation,
      usLeftWWPresence: structure?.scourComp?.usLeftWWPresence,
      usLeftWWCond: structure?.scourComp?.usLeftWWCond,
      usRightWWPresence: structure?.scourComp?.usRightWWPresence,
      usRightWWCond: structure?.scourComp?.usRightWWCond,
      horDebrisStartPct: structure?.scourComp?.horDebrisStartPct,
      horDebrisEndPct: structure?.scourComp?.horDebrisEndPct,
      vertDebrisStartPct: structure?.scourComp?.vertDebrisStartPct,
      vertDebrisEndPct: structure?.scourComp?.vertDebrisEndPct,
      overTopping: structure?.scourComp?.overTopping,
      trafficDelay: structure?.scourComp?.trafficDelay,
      overTopRisk: structure?.scourComp?.overTopRisk,
      overtopDate: structure?.scourComp?.overtopDate,
      highWaterElev: structure?.scourComp?.highWaterElev,
      highWaterDate: structure?.scourComp?.highWaterDate,
      newHighWater: structure?.scourComp?.newHighWater,

      chanProtRating: structure?.userInspection?.chanProtRating,
      scourCondRating: structure?.userInspection?.scourCondRating,
      underwaterRating: structure?.userInspection?.underwaterRating,
      scourVulnerability: structure?.userInspection?.scourVulnerability,
      scpoaReqD: structure?.userInspection?.scpoaReqD,

      scourCritical: structure?.inspection?.scourCritical,
      scourCriticalCat: structure?.inspection?.scourCriticalCat,
      chanRating: structure?.inspection?.chanRating,
      culvRating: structure?.inspection?.culvRating,
      subRating: structure?.inspection?.subRating,
      pierprot: structure?.inspection?.pierprot,
      wateradeq: structure?.inspection?.wateradeq,

      scourCriticalDuration: structure?.inspectionConditionHist?.scourCriticalDuration,
      scourCriticalDurationChanges: structure?.inspectionConditionHist?.scourCriticalDurationChanges,
      chanRatingDuration: structure?.inspectionConditionHist?.chanRatingDuration,
      chanRatingDurationChanges: structure?.inspectionConditionHist?.chanRatingDurationChanges,

      scourCMeasures: structure?.scourCMeasures,
      scourPossCMeasures: structure?.scourPossCMeasures,
      structureUnits: structure?.structureUnits,
      inspectionsUnderwater: structure?.inspectionsUnderwater,
    };
  };

  const getInspLinkDataObject = async (brkey, inspectionId) => {
    //clear idb object
    pageData.value = {};
    let url = INSPECTION_LINKS_ENDPOINT.GET_WATERWAY.replace("{brkey}", brkey).replace("{inspectionId}", inspectionId);

    await axios
      .get(url)
      .then((resp) => {
        convertToInspLinkObject(resp.data.data);
      })
      .catch((e) => {
        LOGGER.logException(e);
      });
  };

  const convertToInspLinkObject = (response) => {
    pageData.value = {
      brkey: response.brkey,
      inspectionId: response.inspectionId,
      updateUserId: response.updateUserId,
      updateDateTime: response.updateDateTime,
      functionClass: response.funcClass,
      servTypUnd: response.servTypUnd,
      navCntrol: response.navCntrol,
      deptMainStrucConfig: response.deptMainStrucConfig,

      // comments
      inspectionComments: response.inspectionComments,

      // scourComp
      scbiSource: response.scourComp?.scbiSource,
      overallOSA: response.scourComp?.overallOSA,
      scourCalculated: response.scourComp?.scourCalculated,
      streamBedMaterial1: response.scourComp?.streamBedMaterial1,
      streamBedMaterial2: response.scourComp?.streamBedMaterial2,
      streamBedMaterialDesc: response.scourComp?.streamBedMaterialDesc,
      unitsInspectedQty: response.scourComp?.unitsInspectedQty,
      nabLocation: response.scourComp?.nabLocation,
      fabLocation: response.scourComp?.fabLocation,
      usLeftWWPresence: response.scourComp?.usLeftWWPresence,
      usLeftWWCond: response.scourComp?.usLeftWWCond,
      usRightWWPresence: response.scourComp?.usRightWWPresence,
      usRightWWCond: response.scourComp?.usRightWWCond,
      horDebrisStartPct: response.scourComp?.horDebrisStartPct,
      horDebrisEndPct: response.scourComp?.horDebrisEndPct,
      vertDebrisStartPct: response.scourComp?.vertDebrisStartPct,
      vertDebrisEndPct: response.scourComp?.vertDebrisEndPct,
      overTopping: response.scourComp?.overTopping,
      trafficDelay: response.scourComp?.trafficDelay,
      overTopRisk: response.scourComp?.overTopRisk,
      overtopDate: response.scourComp?.overtopDate,
      highWaterElev: response.scourComp?.highWaterElev,
      highWaterDate: response.scourComp?.highWaterDate,
      newHighWater: response.scourComp?.newHighWater,

      // page values
      scourCMeasures: response.scourCMeasures,
      scourPossCMeasures: response.scourPossCMeasures,
      structureUnits: response.structureUnits,
      inspectionsUnderwater: response.inspectionsUnderwater,
      scourCriticalDuration: response.scourCriticalDuration,
      chanRatingDuration: response.chanRatingDuration,
      scourCritical: response.scourCritical,
      scourCriticalCat: response.scourCriticalCat,
      chanRating: response.chanRating,
      culvRating: response.culvRating,
      subRating: response.subRating,
      pierprot: response.pierprot,
      wateradeq: response.wateradeq,
      chanProtRating: response.chanProtRating,
      scourCondRating: response.scourCondRating,
      underwaterRating: response.underwaterRating,
      scourVulnerability: response.scourVulnerability,
      scpoaReqD: response.scpoaReqD,
    };
  };

  return {
    getWaterwayObject,
    saveData,
    loadDataObject,
  };
});
